<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useImages } from '@/composables/useImages';

const { t } = useI18n();

interface Props {
  deviceId: string;
  autoRefresh?: boolean;
  refreshInterval?: number; // 秒
  showRoiCalib?: boolean; // 管理者才顯示 ROI 標定入口 (就近放在影像上方)
}

const props = withDefaults(defineProps<Props>(), {
  autoRefresh: true,
  refreshInterval: 60, // 預設 60 秒刷新一次
});

// 批量下載相關狀態
const showBatchDownload = ref(false);
const downloadStartDate = ref('');
const downloadEndDate = ref('');
const downloading = ref(false);
const batchTypes = ref<{ rgb: boolean; thermal: boolean; npz: boolean }>({
  rgb: true,
  thermal: true,
  npz: true,
});

// simulation 設備使用固定圖片
const isSimulation = computed(() => props.deviceId === 'simulation');
const simulationRgbUrl = '/images/simulation/RGB.jpg';
const simulationThermalUrl = '/images/simulation/THERMAL.jpg';

const {
  latestImage,
  loading,
  error,
  hasLatestImage,
  latestRgbThumbnailUrl,
  latestThermalThumbnailUrl,
  fetchLatestImage,
} = useImages();

// 實際顯示的圖片 URL（simulation 使用固定圖片）
const displayRgbUrl = computed(() => {
  if (isSimulation.value) return simulationRgbUrl;
  return latestRgbThumbnailUrl.value;
});

const displayThermalUrl = computed(() => {
  if (isSimulation.value) return simulationThermalUrl;
  return latestThermalThumbnailUrl.value;
});

// 是否有圖片可顯示（simulation 永遠有圖片）
const hasDisplayImage = computed(() => {
  if (isSimulation.value) return true;
  return hasLatestImage.value;
});

// 格式化時間
const formattedCapturedAt = computed(() => {
  // simulation 設備顯示固定文字
  if (isSimulation.value) return t('dashboard.images.staticDemo');
  if (!latestImage.value) return '-';
  const date = new Date(latestImage.value.capturedAt);
  return date.toLocaleString('zh-TW', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
});

// 檔案大小格式化
function formatFileSize(bytes?: number): string {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// 載入圖像
async function loadImage(): Promise<void> {
  if (!props.deviceId) return;
  console.log('[LatestImageCard] 載入圖像 - Device ID:', props.deviceId);
  await fetchLatestImage(props.deviceId);
  console.log('[LatestImageCard] 圖像載入結果:', {
    hasImage: hasLatestImage.value,
    rgbUrl: latestRgbThumbnailUrl.value,
    thermalUrl: latestThermalThumbnailUrl.value,
    error: error.value
  });
}

// 開啟完整圖像
function openFullImage(type: 'rgb' | 'thermal'): void {
  // simulation 設備直接開啟固定圖片
  if (isSimulation.value) {
    const url = type === 'rgb' ? simulationRgbUrl : simulationThermalUrl;
    window.open(url, '_blank');
    return;
  }
  if (!latestImage.value) return;
  const url = type === 'rgb'
    ? latestRgbThumbnailUrl.value.replace('_thumb.jpg', '.jpg').replace('/thumbnails', '')
    : latestThermalThumbnailUrl.value.replace('_thumb.jpg', '.jpg').replace('/thumbnails', '');
  window.open(url, '_blank');
}

// 通用單檔下載
function triggerDownload(url: string, filename: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

const apiBase = import.meta.env.VITE_API_URL || 'http://72.61.117.219:3000';

// 取得最新圖像的完整 URL
function fullPathUrl(path?: string | null): string {
  if (!path) return '';
  const cleaned = path.startsWith('/') ? path.slice(1) : path;
  return `${apiBase}/${cleaned}`;
}

// 下載最新 RGB
async function downloadRgb(): Promise<void> {
  if (!latestImage.value?.rgbImagePath) return;
  const ts = new Date(latestImage.value.capturedAt).toISOString().replace(/[:.]/g, '-');
  triggerDownload(fullPathUrl(latestImage.value.rgbImagePath), `${props.deviceId}_rgb_${ts}.jpg`);
}

// 下載最新熱影像
async function downloadThermal(): Promise<void> {
  if (!latestImage.value?.thermalImagePath) return;
  const ts = new Date(latestImage.value.capturedAt).toISOString().replace(/[:.]/g, '-');
  triggerDownload(fullPathUrl(latestImage.value.thermalImagePath), `${props.deviceId}_thermal_${ts}.jpg`);
}

// 下載最新溫度 RAW (.npy 純陣列 / .npz legacy)
async function downloadNpz(): Promise<void> {
  if (!latestImage.value?.npzFilePath) {
    alert(t('dashboard.images.npzNotAvailable'));
    return;
  }
  const ts = new Date(latestImage.value.capturedAt).toISOString().replace(/[:.]/g, '-');
  const ext = latestImage.value.npzFilePath.endsWith('.npz') ? 'npz' : 'npy';
  triggerDownload(fullPathUrl(latestImage.value.npzFilePath), `${props.deviceId}_thermal_${ts}.${ext}`);
}

const hasNpz = computed(() => !!latestImage.value?.npzFilePath);

// 切換批量下載面板
function toggleBatchDownload(): void {
  showBatchDownload.value = !showBatchDownload.value;

  // 設置默認日期範圍（最近 7 天）
  if (showBatchDownload.value && !downloadEndDate.value) {
    const end = new Date();
    const start = new Date(end);
    start.setDate(start.getDate() - 7);

    downloadStartDate.value = start.toISOString().slice(0, 16);
    downloadEndDate.value = end.toISOString().slice(0, 16);
  }
}

// 批量下載圖像（ZIP）
async function downloadBatchImages(): Promise<void> {
  if (!downloadStartDate.value || !downloadEndDate.value) {
    alert(t('dashboard.images.selectDateRange'));
    return;
  }

  const startDate = new Date(downloadStartDate.value);
  const endDate = new Date(downloadEndDate.value);

  if (startDate > endDate) {
    alert(t('dashboard.images.invalidDateRange'));
    return;
  }

  // 至少選一種類型
  const types: string[] = [];
  if (batchTypes.value.rgb) types.push('rgb');
  if (batchTypes.value.thermal) types.push('thermal');
  if (batchTypes.value.npz) types.push('npz');
  if (types.length === 0) {
    alert(t('dashboard.images.selectAtLeastOneType'));
    return;
  }

  try {
    downloading.value = true;

    const apiUrl = import.meta.env.VITE_API_URL || 'http://72.61.117.219:3000';
    const token = localStorage.getItem('token');

    // 構建查詢參數
    const params = new URLSearchParams({
      from: startDate.toISOString(),
      to: endDate.toISOString(),
      types: types.join(',')
    });

    // 發送下載請求
    const response = await fetch(
      `${apiUrl}/api/images/${props.deviceId}/download?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Download failed');
    }

    // 獲取 blob
    const blob = await response.blob();

    // 創建下載鏈接
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // 從響應頭獲取檔名，或使用默認檔名
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `${props.deviceId}_images_${new Date().toISOString().slice(0, 10)}.zip`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    // 關閉面板
    showBatchDownload.value = false;

    console.log(`✅ 批量下載成功: ${filename}`);

  } catch (error: any) {
    console.error('批量下載失敗:', error);
    alert(`${t('dashboard.images.downloadFailed')}: ${error.message}`);
  } finally {
    downloading.value = false;
  }
}

// 初始載入
onMounted(() => {
  loadImage();

  // 自動刷新
  if (props.autoRefresh && props.refreshInterval > 0) {
    setInterval(loadImage, props.refreshInterval * 1000);
  }
});

// 監聽 deviceId 變化
watch(() => props.deviceId, () => {
  loadImage();
});
</script>

<template>
  <div class="latest-image-card">
    <div class="card-header">
      <h3>📷 {{ t('dashboard.images.title') }}</h3>
      <div class="header-buttons">
        <button @click="downloadRgb" :disabled="!hasLatestImage" class="download-btn rgb-btn" :title="t('dashboard.images.rgb')">
          🖼️ RGB
        </button>
        <button @click="downloadThermal" :disabled="!hasLatestImage" class="download-btn thermal-btn" :title="t('dashboard.images.thermal')">
          🌡️ Thermal
        </button>
        <button @click="downloadNpz" :disabled="!hasNpz" class="download-btn npz-btn" :title="hasNpz ? 'NPY' : t('dashboard.images.npzNotAvailable')">
          📊 NPY
        </button>
        <button @click="toggleBatchDownload" class="batch-download-btn">
          📦 {{ t('dashboard.images.batchDownload') }}
        </button>
        <button v-if="props.showRoiCalib" @click="$router.push('/roi-calibration')" class="download-btn roi-btn">
          🎯 {{ t('dashboard.charts.roiCalib') }}
        </button>
        <button @click="loadImage" :disabled="loading" class="refresh-btn">
          {{ loading ? t('dashboard.images.loading') : t('dashboard.images.refresh') }}
        </button>
      </div>
    </div>

    <!-- 批量下載面板 -->
    <div v-if="showBatchDownload" class="batch-download-panel">
      <div class="panel-header">
        <h4>📦 {{ t('dashboard.images.batchDownloadTitle') }}</h4>
        <button @click="showBatchDownload = false" class="close-btn">{{ t('dashboard.images.close') }}</button>
      </div>
      <div class="date-range-selector">
        <div class="date-input-group">
          <label>{{ t('dashboard.images.startTime') }}:</label>
          <input
            type="datetime-local"
            v-model="downloadStartDate"
            class="date-input"
          />
        </div>
        <div class="date-input-group">
          <label>{{ t('dashboard.images.endTime') }}:</label>
          <input
            type="datetime-local"
            v-model="downloadEndDate"
            class="date-input"
          />
        </div>
      </div>

      <!-- 類型選擇 (RGB / Thermal / NPY) -->
      <div class="type-selector">
        <label class="type-checkbox">
          <input type="checkbox" v-model="batchTypes.rgb" />
          🖼️ RGB
        </label>
        <label class="type-checkbox">
          <input type="checkbox" v-model="batchTypes.thermal" />
          🌡️ Thermal
        </label>
        <label class="type-checkbox">
          <input type="checkbox" v-model="batchTypes.npz" />
          📊 {{ t('dashboard.images.npyRaw') }}
        </label>
      </div>
      <div class="panel-actions">
        <button
          @click="downloadBatchImages"
          :disabled="downloading || !downloadStartDate || !downloadEndDate"
          class="confirm-download-btn"
        >
          {{ downloading ? t('dashboard.images.downloading') : `🗜️ ${t('dashboard.images.downloadZip')}` }}
        </button>
        <button @click="showBatchDownload = false" class="cancel-btn">
          {{ t('dashboard.images.cancel') }}
        </button>
      </div>
      <p class="panel-hint">
        💡 {{ t('dashboard.images.downloadHint') }}
      </p>
    </div>

    <!-- 錯誤提示 -->
    <div v-if="error" class="error-message">
      ⚠️ {{ error }}
    </div>

    <!-- 載入中 -->
    <div v-else-if="loading && !hasLatestImage" class="loading">
      <div class="spinner"></div>
      <p>{{ t('dashboard.images.loading') }}</p>
    </div>

    <!-- 無圖像 -->
    <div v-else-if="!hasDisplayImage" class="no-image">
      <p>📭 {{ t('dashboard.images.noImage') }}</p>
      <p class="hint">{{ t('dashboard.images.hint') }}</p>
    </div>

    <!-- 圖像顯示 -->
    <div v-else class="image-container">
      <!-- 拍攝時間 -->
      <div class="captured-time">
        <span class="label">{{ t('dashboard.images.capturedAt') }}:</span>
        <span class="value">{{ formattedCapturedAt }}</span>
      </div>

      <!-- 圖像網格 -->
      <div class="image-grid">
        <!-- RGB 圖像 -->
        <div class="image-item">
          <div class="image-label">🖼️ {{ t('dashboard.images.rgb') }}</div>
          <div class="image-wrapper" @click="openFullImage('rgb')">
            <img
              v-if="displayRgbUrl"
              :src="displayRgbUrl"
              :alt="t('dashboard.images.rgb')"
              class="thumbnail"
            />
            <div v-else class="image-placeholder">{{ t('dashboard.images.noImage') }}</div>
            <div class="image-overlay">
              <span>🔍 {{ t('dashboard.images.clickToEnlarge') }}</span>
            </div>
          </div>
          <div class="image-info" v-if="!isSimulation">
            <span>{{ t('dashboard.images.fileSize') }}: {{ formatFileSize(latestImage?.rgbFileSize) }}</span>
          </div>
          <div class="image-info" v-else>
            <span>{{ t('dashboard.images.demoImage') }}</span>
          </div>
        </div>

        <!-- 熱影像 -->
        <div class="image-item">
          <div class="image-label">🌡️ {{ t('dashboard.images.thermal') }}</div>
          <div class="image-wrapper" @click="openFullImage('thermal')">
            <img
              v-if="displayThermalUrl"
              :src="displayThermalUrl"
              :alt="t('dashboard.images.thermal')"
              class="thumbnail"
            />
            <div v-else class="image-placeholder">{{ t('dashboard.images.noImage') }}</div>
            <div class="image-overlay">
              <span>🔍 {{ t('dashboard.images.clickToEnlarge') }}</span>
            </div>
          </div>
          <div class="image-info" v-if="!isSimulation">
            <span>{{ t('dashboard.images.fileSize') }}: {{ formatFileSize(latestImage?.thermalFileSize) }}</span>
          </div>
          <div class="image-info" v-else>
            <span>{{ t('dashboard.images.demoImage') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.latest-image-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e0e0e0;
}

.card-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.header-buttons {
  display: flex;
  gap: 8px;
}

.refresh-btn,
.download-btn,
.batch-download-btn {
  padding: 6px 12px;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.3s;
}

.rgb-btn { background: #2196F3; }
.download-btn.roi-btn { background: #FFC107; color: #263238; font-weight: 600; }
.rgb-btn:hover:not(:disabled) { background: #1976D2; }
.thermal-btn { background: #FF9800; }
.thermal-btn:hover:not(:disabled) { background: #F57C00; }
.npz-btn { background: #9C27B0; }
.npz-btn:hover:not(:disabled) { background: #7B1FA2; }
.batch-download-btn { background: #607D8B; }
.batch-download-btn:hover:not(:disabled) { background: #455A64; }

.download-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.type-selector {
  display: flex;
  gap: 16px;
  margin: 12px 0;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 6px;
}

.type-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
  font-size: 14px;
}

.type-checkbox input {
  cursor: pointer;
}

.refresh-btn {
  background: #4CAF50;
}

.refresh-btn:hover:not(:disabled) {
  background: #45a049;
}

.refresh-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.download-btn {
  background: #2196F3;
}

.download-btn:hover:not(:disabled) {
  background: #0b7dda;
}

.download-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.batch-download-btn {
  background: #FF9800;
}

.batch-download-btn:hover {
  background: #F57C00;
}

.error-message {
  padding: 12px;
  background: #ffebee;
  color: #c62828;
  border-radius: 4px;
  text-align: center;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #666;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4CAF50;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.no-image {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.no-image p {
  margin: 8px 0;
}

.no-image .hint {
  font-size: 14px;
  color: #bbb;
}

.image-container {
  margin-top: 16px;
}

.captured-time {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 8px;
  background: #f5f5f5;
  border-radius: 4px;
  font-size: 14px;
}

.captured-time .label {
  font-weight: 600;
  color: #666;
}

.captured-time .value {
  color: #333;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.image-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.image-label {
  font-weight: 600;
  font-size: 14px;
  color: #555;
  text-align: center;
}

.image-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 4/3;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  background: #f0f0f0;
  transition: transform 0.3s;
}

.image-wrapper:hover {
  transform: scale(1.02);
}

.thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  background: #f5f5f5;
}

.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px;
  text-align: center;
  opacity: 0;
  transition: opacity 0.3s;
  font-size: 14px;
}

.image-wrapper:hover .image-overlay {
  opacity: 1;
}

.image-info {
  text-align: center;
  font-size: 12px;
  color: #888;
}

/* 批量下載面板 */
.batch-download-panel {
  margin-top: 16px;
  padding: 16px;
  background: #f8f9fa;
  border: 2px solid #FF9800;
  border-radius: 8px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.panel-header h4 {
  margin: 0;
  font-size: 16px;
  color: #333;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 20px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  line-height: 1;
}

.close-btn:hover {
  color: #333;
}

.date-range-selector {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
}

.date-input-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.date-input-group label {
  font-size: 14px;
  font-weight: 600;
  color: #555;
}

.date-input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
}

.date-input:focus {
  outline: none;
  border-color: #FF9800;
  box-shadow: 0 0 0 2px rgba(255, 152, 0, 0.1);
}

.panel-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.confirm-download-btn {
  flex: 1;
  padding: 10px 20px;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;
}

.confirm-download-btn:hover:not(:disabled) {
  background: #45a049;
}

.confirm-download-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.cancel-btn {
  padding: 10px 20px;
  background: #f44336;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.3s;
}

.cancel-btn:hover {
  background: #da190b;
}

.panel-hint {
  margin: 0;
  padding: 8px;
  background: #fff3e0;
  border-left: 3px solid #FF9800;
  font-size: 13px;
  color: #666;
  border-radius: 4px;
}

@media (max-width: 768px) {
  .image-grid {
    grid-template-columns: 1fr;
  }

  .date-range-selector {
    grid-template-columns: 1fr;
  }

  .header-buttons {
    flex-wrap: wrap;
  }
}
</style>
